import type { PgTimestamp } from "@/lib/api";

// Format helpers shared across pages. Keep these tiny — i18n / locale will
// happen as a single pass in Day 23.

export function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}

// Quota-aware number formatter: scheduler/jobs writes INT32_MAX (2147483647)
// to system_state for manual_static platforms (X/TikTok in Phase 1) to mean
// "unlimited". Displaying 2.1B confuses operators; collapse to a label.
const QUOTA_UNLIMITED = 2147483647;
export function fmtQuota(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= QUOTA_UNLIMITED) return "∞ (manual)";
  return n.toLocaleString();
}

export function fmtPercent(n: number | null | undefined, digits = 1): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtRank(pct: number | null | undefined): string {
  // pct comes from PERCENT_RANK()*100 so it lives on a 0-100 scale where
  // 100 = highest in cohort. Display as "top N%" (smaller = better).
  if (pct === null || pct === undefined || Number.isNaN(pct)) return "—";
  const top = 100 - pct;
  return `top ${top.toFixed(0)}%`;
}

// Go's time.Time zero value serializes as "0001-01-01T00:00:00Z". Scheduler
// status endpoints surface that when a job has never run. Render it as "—"
// so the UI doesn't show a confusing year-1 timestamp.
const GO_ZERO_TIME = "0001-01-01T00:00:00Z";

export function fmtTimestamp(t: PgTimestamp | null | undefined): string {
  if (!t || t === GO_ZERO_TIME) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1900) return "—";
  return d.toLocaleString();
}

export function fmtDate(t: PgTimestamp | null | undefined): string {
  if (!t || t === GO_ZERO_TIME) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1900) return "—";
  return d.toLocaleDateString();
}

// fmtUntilNext renders the gap to a future timestamp as "X 小时后" / "X 分后"
// / "已过期 X 分". Used for the System page cron "next run" column so the
// operator can see "next fires in 12 min" without subtracting from now in
// their head. Empty / past-by-more-than-an-hour falls back to "—".
export function fmtUntilNext(t: PgTimestamp | null | undefined): string {
  if (!t || t === GO_ZERO_TIME) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return "—";
  const deltaMs = d.getTime() - Date.now();
  const mins = Math.round(deltaMs / 60_000);
  if (mins < -60) return "—"; // probably stale; cron has since refired
  if (mins < 0) return `已过 ${Math.abs(mins)} 分`;
  if (mins < 1) return "随时";
  if (mins < 60) return `${mins} 分后`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs < 24) return rem > 0 ? `${hrs} 时 ${rem} 分后` : `${hrs} 时后`;
  const days = Math.floor(hrs / 24);
  return `${days} 天后`;
}

// Compact relative-time for "last contacted" cells. "今天" / "X 天前" /
// "X 月前" / "X 年前". Falls back to "—" for missing or zero-time inputs.
export function fmtRelativeDays(t: PgTimestamp | null | undefined): string {
  if (!t || t === GO_ZERO_TIME) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 1900) return "—";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 0) return "未来";
  if (days === 0) return "今天";
  if (days < 30) return `${days} 天前`;
  if (days < 365) return `${Math.floor(days / 30)} 月前`;
  return `${Math.floor(days / 365)} 年前`;
}

export function platformLabel(p: string): string {
  switch (p) {
    case "youtube":
      return "YouTube";
    // DB stores X platform as "twitter" (Phase 1 schema: PlatformX = "twitter").
    // Accept both forms so the bug we hit in v2.0.1 doesn't recur if some
    // caller passes "x" — they get the same operator-facing label either way.
    case "twitter":
    case "x":
      return "X";
    case "tiktok":
      return "TikTok";
    case "rss":
      return "RSS";
    case "foresight":
      return "Foresight";
    case "panews":
      return "PA News";
    default:
      return p;
  }
}

// Base64-encoded JSONB columns (sponsorship snapshot, evaluation snapshot, etc.)
// arrive as base64 strings until backend adds custom marshalers in Day 22.
//
// IMPORTANT: `atob` returns a JS "binary string" where each char is a raw
// byte (0x00-0xFF). Calling JSON.parse on that string treats those bytes as
// UTF-16 code units and mangles every multi-byte UTF-8 sequence (every
// Chinese / Japanese / Korean / emoji char in the payload). Decode the
// bytes through TextDecoder so the string parses as UTF-8 like it would
// in any non-browser JSON pipeline.
export function decodeJSONB<T = unknown>(s: string | null | undefined): T | null {
  if (!s) return null;
  try {
    let decoded: string;
    if (typeof atob === "function") {
      const binary = atob(s);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      decoded = new TextDecoder("utf-8").decode(bytes);
    } else {
      decoded = Buffer.from(s, "base64").toString("utf8");
    }
    return JSON.parse(decoded) as T;
  } catch {
    return null;
  }
}
